const PART_SEMANTICS = Object.freeze({
    root: "root", label: "label",
    // Controls and the things that operate them.
    control: "input", startControl: "input", endControl: "input", search: "input",
    // A timepicker's `hour` and `minute` are the segments the header lays out — containers. The
    // element a user types into is the control inside each, named separately so the contract reaches
    // it: a segment holding a `<div>` where the input belongs is a widget nothing can operate.
    hour: "group", minute: "group", hourControl: "input", minuteControl: "input",
    hexInput: "input", nativePicker: "input",
    // The value a native submit reads, and nothing else. It is an `<input>` and it is not a control:
    // nobody focuses it, nobody names it, and a screen reader never reaches it — so it answers to
    // `submission` rather than `input`, which would require it to have an accessible name it can
    // neither carry nor need. Kinds declare one only where no real control holds the value.
    submitFalse: "submission",
    // The trigger is the widget's control surface, not a plain button: it carries `role="combobox"`,
    // and a native `<select>` satisfies it too.
    trigger: "input",
    toggle: "button", clear: "button",
    modeToggle: "button", action: "button", optionStep: "button",
    // A chip in the strip is what was chosen, and it carries the controls for changing that: a remove,
    // and in counter mode the two steppers. A chip that were itself a button could hold none of them —
    // a button inside a button is neither valid nor announceable — and a person reading "3" would have
    // to reopen the popup and find the row again to make it 2, which is the journey the strip removes.
    // The widget's own layout box, inside the shell every kind sits in. Named rather than folded into
    // `inputWrapper`, which means the *shell's* box for every other kind — one name for two different
    // elements is how a height comparison came to be off by the border a theme draws on one of them.
    box: "presentation",
    chip: "container",
    // What the chip is made of: a name, how many, and the control that takes it off.
    chipRemove: "button", chipMove: "button", chipStep: "button",
    // Taking every choice off, and putting the last destructive act back. Both are pressed, so both
    // are buttons, and both sit at the field's trailing edge: the way back is the remedy for the
    // clear-all, which is what lets a control discard everything without asking first.
    clearAll: "button", overflowCount: "button", wayBackAction: "button",
    // Text, with the role the contract declares: what a chip is called when the strip cut it short.
    chipTooltip: "text",
    // Announcements.
    errors: "status", loading: "status", empty: "status", errorItem: "status",
    // What was chosen, said out loud. Nothing on screen: the strip is the sighted confirmation.
    announcement: "status",
    // A file the field turned away is not an error of the value — the field holds what it accepted and
    // is valid — but it is an answer to something the person just did, so it announces itself.
    rejected: "status",
    // An icon carrying its message as a label, not a live region: the message itself already reaches
    // assistive technology through the control's `aria-describedby`, and announcing it twice is worse
    // than announcing it once.
    inlineError: "image",
    // Supporting text describes the control; it is not an announcement, and it carries no live role.
    supportingText: "text",
    // Text the user reads.
    value: "text", placeholder: "text", optionLabel: "text", optionText: "text", optionCount: "text",
    // The words inside a value chip, and how many of that value is held. The entry in the list
    // has carried both from the start; the chip drew them and the contract had no name for them,
    // so nothing could ask a renderer whether it drew them at all — and one renderer did not.
    chipLabel: "text", chipCount: "text",
    separator: "text", requiredMarker: "text",
    weekday: "columnheader",
    // Decoration: it carries meaning for the eye, and none for assistive technology.
    arrow: "presentation", indicator: "presentation", thumb: "presentation", preview: "presentation",
    optionCheck: "presentation", optionControl: "presentation", dialHand: "presentation",
    // Drawn surfaces with nothing to operate: the layer of dimmed stretches and each stretch in it.
    dialUnavailable: "presentation", dialUnavailableArc: "presentation",
    dialFace: "presentation",
    // The numbers are painted on the face; the face takes the pointer, so they announce nothing.
    dialNumber: "presentation",
    // Structures with their own semantics.
    listbox: "listbox", option: "option", swatch: "option", popup: "popup", calendar: "popup",
    clock: "popup", dialog: "dialog", grid: "grid", gridcell: "gridcell",
    // The month and year views are grids of choices, like the day grid they replace — so a keyboard
    // user meets the same structure whichever view is showing, rather than a run of bare buttons.
    monthPicker: "grid", monthCell: "gridcell", yearPicker: "grid", yearCell: "gridcell",
    // Containers the contract deliberately leaves unconstrained.
    group: "group", inputWrapper: "group", prefix: "group", suffix: "group", container: "group",
    content: "group", header: "group", dialogHeader: "group",
    // The calendar's header. Declared rather than left to presentation classes: three renderers
    // shipped operable buttons in a panel whose anatomy could not see them, so the door asking
    // whether a popup holds an action answered about parts that were not there. The middle one
    // is named for the act it performs, not the place it sits. ADR 0212.
    headerPrevious: "button", viewToggle: "button", headerTitle: "text", headerNext: "button", actions: "group", chips: "group",
    // The single row a grid structures its cells in. ADR 0148.
    chipRow: "group",
    options: "group", optionWrapper: "group", dropzone: "group", fileList: "group",
    // A palette you pick one colour from. All three renderers say `role="listbox"` over
    // `role="option"` swatches, which is what it is; calling it an unconstrained group let the
    // contract have no opinion about a widget every renderer had already agreed on.
    presets: "listbox",
    fileItem: "group", weekdays: "group", row: "group", track: "group", period: "group",
    // Two buttons showing both halves of the day, one of them marked. A `radiogroup` in ARIA terms:
    // the value is visible rather than inferred from the label of a control that toggles it.
    periodOption: "button",
});
export function semanticElement(partName) {
    const semantic = PART_SEMANTICS[partName];
    if (!semantic) {
        throw new RangeError(`[modyra] Part "${partName}" has no declared semantic. Add it to PART_SEMANTICS — a part the ` +
            `contract has no opinion about admits every element, which is not a contract.`);
    }
    return semantic;
}
/**
 * Variation the contract permits, and why.
 *
 * Measured across all three renderers in the resting state. Each of these is a part left `optional`
 * on purpose; without the reason written down, "optional" is indistinguishable from "nobody asked".
 *
 * - `select.value` and `multiselect.placeholder` are an either/or: a renderer shows the chosen value
 *   or, when there is none, the placeholder. Both parts exist; only one is on screen at a time.
 * - `supportingText` may be replaced by the error list rather than shown beside it. Renderers differ,
 *   and both are defensible — which is why a projection is told whether the description was rendered
 *   rather than assuming it.
 * - `file.clear` and `file.fileList` are a feature choice: a file field may offer to clear its
 *   selection and to list what was chosen, and one that does neither is still a file field.
 * - `file.rejected` is on screen only when the field has just turned a candidate away, which is a
 *   state and not a feature: a field that has refused nothing shows nothing there.
 *
 * One measured difference is *not* covered here and is not a rendering question: the numeric kinds
 * start at `0` in one renderer and `null` in another, so `required` passes on one and fails on the
 * other. That is a value-semantics disagreement and belongs with the value dimension, not the DOM.
 */
